import { TaskTemplateScope } from '@crew/enums/domain'
import { useTranslation } from '@crew/modules/i18n'
import { ValidateRules } from '@crew/utils/form'
import { useTaskTemplateScopeDataSource } from 'hooks/dataSource/useResourceDataSource'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { TreeListTaskTemplateTask } from './components/taskTemplateTaskListAndEntryForm/taskTemplateTaskListAndEntryForm'
import {
  useDeleteTaskTemplateMutation,
  useInsertTaskTemplateMutation,
  useUpdateTaskTemplateMutation,
} from '@crew/apis/taskTemplate/taskTemplateApis'
import { InsertTaskTemplateRequest } from '@crew/apis/taskTemplate/models/insertTaskTemplate/request'
import { UpdateTaskTemplateRequest } from '@crew/apis/taskTemplate/models/updateTaskTemplate/request'

// フォームの型定義
// タスクテンプレート本体
export type FormValues = {
  name: string | null
  scope: TaskTemplateScope | null
}

const initialValues: FormValues = {
  name: null,
  // 初期値はプライベート
  scope: TaskTemplateScope.Private,
}

export const useTaskTemplateEntryForm = () => {
  const { t } = useTranslation()

  const [
    insertTaskTemplateMutation,
    { isLoading: isLoadingInsertTaskTemplate },
  ] = useInsertTaskTemplateMutation()
  const [
    updateTaskTemplateMutation,
    { isLoading: isLoadingUpdateTaskTemplate },
  ] = useUpdateTaskTemplateMutation()
  const [
    deleteTaskTemplateMutation,
    { isLoading: isLoadingDeleteTaskTemplate },
  ] = useDeleteTaskTemplateMutation()

  const { control, handleSubmit, reset, formState, clearErrors } =
    useForm<FormValues>({
      criteriaMode: 'all',
      defaultValues: initialValues,
    })

  const insertTaskTemplate = useCallback(
    async (
      data: FormValues,
      taskTemplateTasks: TreeListTaskTemplateTask[],
      projectId: string
    ) => {
      // validateされるはずだが、nullの場合はreturnする
      if (data.name === null || data.scope === null) return

      // タスクテンプレート登録API
      const param: InsertTaskTemplateRequest = {
        name: data.name,
        scope: data.scope,
        projectId: projectId,
        taskTemplateTasks: taskTemplateTasks.map((task) => ({
          subject: task.subject,
          taskKindId: task.taskKindId,
          taskPriority: task.taskPriority,
          assignToUserId: task.assignToUserId ?? undefined,
          requiredDays: task.requiredDays ?? undefined,
          description: task.description ?? undefined,
          taskOrder: task.taskOrder,
        })),
      }

      await insertTaskTemplateMutation(param).unwrap()

      // フォームリセット
      reset()
      clearErrors()
    },
    [clearErrors, insertTaskTemplateMutation, reset]
  )

  const updateTaskTemplate = useCallback(
    async (
      taskTemplateId: string,
      version: number,
      data: FormValues,
      taskTemplateTasks: TreeListTaskTemplateTask[]
    ) => {
      // validateされるはずだが、nullの場合はreturnする
      if (data.name === null || data.scope === null) return

      // タスクテンプレート更新API
      const param: UpdateTaskTemplateRequest = {
        id: taskTemplateId,
        name: data.name,
        scope: data.scope,
        taskTemplateTasks: taskTemplateTasks.map((task) => ({
          subject: task.subject,
          taskKindId: task.taskKindId,
          taskPriority: task.taskPriority,
          assignToUserId: task.assignToUserId ?? undefined,
          requiredDays: task.requiredDays ?? undefined,
          description: task.description ?? undefined,
          taskOrder: task.taskOrder,
        })),
        version: version,
      }

      await updateTaskTemplateMutation(param).unwrap()

      // フォームリセット
      reset()
      clearErrors()
    },
    [clearErrors, reset, updateTaskTemplateMutation]
  )

  const deleteTaskTemplate = useCallback(
    async (taskTemplateId: string) => {
      // タスクテンプレート削除API
      await deleteTaskTemplateMutation({
        id: taskTemplateId,
      }).unwrap()
    },
    [deleteTaskTemplateMutation]
  )

  const validateRules: ValidateRules<FormValues> = useMemo(
    () => ({
      name: {
        required: t('message.general.required'),
        maxLength: {
          value: 100,
          message: t('message.general.maxLength', {
            name: t('label.taskTemplate.templateName'),
            value: 100,
          }),
        },
      },
      scope: {
        required: t('message.general.required'),
      },
    }),
    [t]
  )

  const taskTemplateDataSource = useTaskTemplateScopeDataSource()

  return {
    validateRules,
    control,
    handleSubmit,
    reset,
    insertTaskTemplate,
    isLoadingInsertTaskTemplate,
    updateTaskTemplate,
    isLoadingUpdateTaskTemplate,
    deleteTaskTemplate,
    isLoadingDeleteTaskTemplate,
    taskTemplateDataSource,
    formState,
  }
}
